/**
 * useBPAFindingsInsights
 *
 * Fetches server-computed finding insights from
 * GET /bpa/findings/insights/ and writes the result into
 * `bpaInsightsAtom`.
 *
 * Mirrors the pattern used by `useBPAFindings`:
 *  - Waits for query-string filter initialisation before first fetch.
 *  - Re-fetches when query params change (filters, refresh triggers).
 *  - Cancels stale responses via the `cancelled` guard.
 */

import { useAtom, useAtomValue } from 'jotai';
import { extractApiErrorMessage } from 'lib/helper';
import { showFailureToast } from 'lib/ToastHelper';
import { useEffect } from 'react';

import { fetchBPAFindingsInsights } from '../services/bpaFindingsService';
import {
  bpaInsightsAtom,
  insightsQueryParamsAtom,
  isQuerystringFiltersInitializedAtom,
} from '../state';

export function useBPAFindingsInsights() {
  const queryParams = useAtomValue(insightsQueryParamsAtom);
  const isInitialized = useAtomValue(isQuerystringFiltersInitializedAtom);
  const [, setInsights] = useAtom(bpaInsightsAtom);

  useEffect(() => {
    // Wait until URL query-string filters have been read and applied
    if (!isInitialized) return;

    let cancelled = false;

    const loadInsights = async () => {
      setInsights((prev) => ({ ...prev, loading: true }));

      try {
        const response = await fetchBPAFindingsInsights(queryParams);

        if (!cancelled) {
          setInsights({
            insights: response.data ?? null,
            loading: false,
          });
        }
      } catch (err) {
        if (!cancelled) {
          showFailureToast(
            extractApiErrorMessage(
              err,
              'Failed to load BPA findings insights.',
            ),
          );
          setInsights({
            insights: null,
            loading: false,
          });
        }
      }
    };

    void loadInsights();

    return () => {
      cancelled = true;
    };
  }, [queryParams, isInitialized, setInsights]);
}
